/******************************************************************************
 *
 * Copyright (c) 2019-2022 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Shivam Mishra' <shivam.mishra@wakencode.com>, June 2022
 *
 *****************************************************************************/

import React, { useEffect, useState } from "react";
import Styles from "./PersonalDetails.module.css";
import { prepareOwnerPostData, preparePersonalDetailsPostData } from "../../container/postDataFormat";
import { getUserInfo, updateInfo, verifyUserEmailStatus } from "../../network";
import { string } from "../../constant/string";
import Loader from "../../loader/WhiteLoader";
import { useAuth } from "../../context/AuthContext";
import { useUser } from "../../context/UserContext";
import { SALoader } from "../../loader/SALoader";
import { retryApiCall } from "../../utilities/retry";

/**
 * Personal details page called in Wrapper.js
 * This page takes user details and update in
 * local storage and api
 *
 * @param {String} setPage State for current page
 * @param {String} jobRole State for job role
 * @param {String} setJobRole Method to update state for job role
 * @param {Boolean} isInvited State for user invitaion
 * @param {Function} setIsInvited setState for user invitaion
 * @returns JSX for the personal details/your details page
 */
const PersonalDetails = ({ setPage, jobRole, setJobRole, isInvited, setIsInvited }) => {
  // State for loader
  const [loading, setLoading] = useState(false);
  // State for activation loader
  const [pageLoading, setPageLoading] = useState(false);
  // State for user info
  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    employeeRole: "",
  });

  // State to set token from auth context
  const { token } = useAuth("");
  //State to set user data from user context
  const { userData, setUserData } = useUser("");

  // State for user input error info
  const [userInfoError, setUserInfoError] = useState({
    firstName: "",
    lastName: "",
    email: "",
    jobRole: "",
    saveUserData: "",
  });

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  useEffect(() => {
    //Get current user information from API
    callApiToGetData();
  }, []);

  /**
   * Handle input change
   *
   * @param {MouseEvent} e Input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Set user information
    setUserInfo((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
    // Set error for user information
    setUserInfoError((prevState) => {
      return {
        ...prevState,
        [name]: "",
      };
    });
  };

  /**
   * Validate user entered email
   *
   * @param {email} email User entered email id
   * @returns {boolean} Valid email or not
   */
  const validateEmail = (email) => {
    let format = /\S+@\S+\.\S+/;
    return format.test(email);
  };

  /**
   * Call get user api to get the previously saved user info
   */
  const callApiToGetData = () => {
    setPageLoading(true);

    // Call api to get user information
    retryApiCall(getUserInfo, config, 3, 2000)
      .then((response) => {
        let userApiData = response.data;
        setIsInvited(false);
        if (userApiData) {
          setUserData((prev) => {
            return { ...prev, ...userApiData };
          });
          setUserInfo(userApiData?.userDetails);
          setJobRole(userApiData?.userDetails?.employeeRole === "CXO" ? "owner" : "employee");
          if (userApiData?.invitationId) {
            setIsInvited(true);
          }
        }
        setPageLoading(false);
      })
      .catch((err) => {
        setPageLoading(false);
      });
  };

  /**
   * Call api to check status of user mobile number
   */
  const callApiToVerifyUserEmailStatus = () => {
    setLoading(true);
    // Prepare correct format for request body
    let postData = {
      email: userInfo.email,
    };

    // Call api to verify user mobile
    verifyUserEmailStatus(postData)
      .then(() => {
        // Call api to save all the user information
        callApiToSaveData();
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.errorDescription) {
          // Update error state
          setApiError("email", `${string.EMAIL_ALREADY_USED}`);
        }
        setLoading(false);
      });
  };

  /**
   * Call update api to save all the user entered information
   */
  const callApiToSaveData = () => {
    setLoading(true);
    // Prepare correct format for request body
    let postData =
      jobRole === "owner"
        ? prepareOwnerPostData(userInfo, userData)
        : preparePersonalDetailsPostData(userInfo, userData);
    // Call api to update/save user information
    updateInfo(postData, config)
      .then(() => {
        if (isInvited && !userInfo.email && userInfo.employeeRole === "TECHNICIAN") {
          // Set current page to work details information page
          setPage("WorkSizeDetails");
        } else if (
          isInvited &&
          userInfo.email == userData?.userDetails?.email &&
          userInfo.email === userData?.verifiedEmail
        ) {
          // Set current page to work size details information page
          setPage("WorkSizeDetails");
        } else if (
          isInvited &&
          (userInfo.email != userData?.userDetails?.email || userInfo.email != userData?.verifiedEmail)
        ) {
          setPage("EmailVerification");
        } else if (
          !isInvited &&
          userInfo.email == userData?.userDetails?.email &&
          userInfo.email === userData?.verifiedEmail
        ) {
          // Set current page to work details information page
          setPage("WorkDetails");
        } else {
          // Set current page to work details empolyee count page
          setPage("EmailVerification");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        if (err?.response?.status === 500) {
          setApiError("saveUserData", string.SOMETHING_WENT_WRONG_TRY_AGAIN);
        }
      });
  };

  /**
   * Update error state for invalid/empty user input
   *
   * @param {String} name User information object key
   * @param {*} value User information object value
   */
  const setApiError = (name, value) => {
    setUserInfoError((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  /**
   * Validate user information if valid or not
   *
   * @returns {Boolean} User input are valid or not
   */
  const validateAllInput = () => {
    let flag = true;
    if (!userInfo.firstName) {
      flag = false;
      // Set first name error
      setApiError("firstName", string.REQUIRED);
    }
    if (!userInfo.lastName) {
      flag = false;
      // Set last name error
      setApiError("lastName", string.REQUIRED);
    }

    if (!userInfo.email && userInfo.employeeRole !== "TECHNICIAN") {
      flag = false;
      // Set email id error
      setApiError("email", string.REQUIRED);
    }

    if (userInfo.email) {
      let emailValid = validateEmail(userInfo.email);
      if (!emailValid) {
        flag = false;
        // Set email id error
        setApiError("email", string.ENTER_VALID_EMAIL);
      }
    }
    if (!isInvited && !jobRole) {
      flag = false;
      // Set job role error
      setApiError("jobRole", string.REQUIRED);
    }
    return flag;
  };

  /**
   * Next button handler
   *
   * @param {*} e Event for Next button click
   */
  const handleNextButtonChange = (e) => {
    e.preventDefault();

    let isValid = validateAllInput();

    // Save user information to local storage
    setUserData((prev) => ({
      ...(prev || {}),
      userDetails: {
        ...(prev?.userDetails || {}),
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        employeeRole: userInfo.employeeRole,
      },
      isEmailVerified:
        userInfo.email == userData?.userDetails?.email &&
        userInfo.email == userData?.verifiedEmail &&
        userData?.isEmailVerified
          ? true
          : false,
    }));

    if (isValid) {
      if (userInfo.email) {
        // Call api to verify user email status
        callApiToVerifyUserEmailStatus();
      } else if (!userInfo.email && userInfo.employeeRole == "TECHNICIAN") {
        callApiToSaveData();
      }
    }
  };

  /**
   * Back button handler
   *
   * @param {*} e Event for Back button click
   */
  const handleBackButtonChange = (e) => {
    e.preventDefault();
    setPage("SignUp");
  };
  /**
   * handle job role
   *
   * @param {*} e Event for button click
   * @param {*} para Value for job role
   */
  const handleJobRoleButtonChange = (e, para) => {
    e.preventDefault();
    setJobRole(para);
    if (para === "owner") {
      setUserInfo((prev) => ({ ...prev, employeeRole: "CXO" }));
    }
    // Set job role error
    setApiError("jobRole", "");
  };

  return (
    <>
      {pageLoading ? (
        <div className="adjustLoaderForFullScreen">
          <SALoader />
        </div>
      ) : (
        <div className="main">
          {/* Head text */}
          <div className="headText">{string.YOUR_DETAILS}</div>
          {/* Line under head text */}
          <div className="line"></div>
          {/* Sub-heading under line */}
          <div className="text">{string.ENTER_YOUR_PERSONAL_DETAILS_TO_PROCEED} </div>
          {/* input fields for user details */}
          <div className="flexContainer">
            {/* First name input field */}
            <div className="tenREM">
              <input
                className={Styles.firstName}
                onChange={handleInputChange}
                placeholder={string.FIRST_NAME}
                name="firstName"
                value={userInfo.firstName}
                id={userInfoError.firstName ? "redBorder" : "greyBorder"}
              ></input>
              {userInfoError.firstName ? <p className="errorText"> {userInfoError.firstName} </p> : null}
            </div>
            {/* Last name input field */}
            <div className="tenREM">
              <input
                className={Styles.lastName}
                onChange={handleInputChange}
                placeholder={string.LAST_NAME}
                name="lastName"
                value={userInfo.lastName}
                id={userInfoError.lastName ? "redBorder" : "greyBorder"}
              ></input>
              {userInfoError.lastName ? <p className="errorText"> {userInfoError.lastName} </p> : null}
            </div>
          </div>
          {/* Email id input field */}
          <div className="tenREM">
            <input
              onChange={handleInputChange}
              type="email"
              placeholder={string.EMAIL_ADDRESS}
              name="email"
              value={userInfo.email}
              className={userInfoError.email ? "wrongInput" : "input"}
            ></input>
            {userInfoError.email && userInfoError.email.includes("\n") && (
              <p className="errorTextModifiedForEmailError">
                {" "}
                {userInfoError.email.split("\n").map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            )}
            {userInfoError.email && !userInfoError.email.includes("\n") && (
              <p className="errorText"> {userInfoError.email}</p>
            )}
          </div>
          {isInvited ? (
            // User role field if invited
            <div className="tenREM">
              <input
                value={userInfo.employeeRole}
                className="disabled-input"
                id={Styles[userInfo.employeeRole]}
                readOnly
              ></input>
              {userInfoError.saveUserData ? <p className="errorText"> {userInfoError.saveUserData} </p> : null}
            </div>
          ) : (
            <div>
              {/* Prefix for user role  */}
              <div className={Styles.selectText}>{string.I_AM_AN}</div>
              {/* Options for user role */}
              <div className="tenREM">
                <div className={Styles.halfWidthbtn}>
                  {/* <button
                    className={Styles.employee}
                    id={jobRole === "employee" ? "greenBorder" : "greyBorder"}
                    onClick={(e) => handleJobRoleButtonChange(e, "employee")}
                    disabled
                  >
                    {string.EMPLOYEE}
                  </button> */}
                  <button
                    className={Styles.owner}
                    id={jobRole === "owner" ? "greenBorder" : "greyBorder"}
                    onClick={(e) => handleJobRoleButtonChange(e, "owner")}
                  >
                    {string.OWNER}
                  </button>
                </div>
                {userInfoError.jobRole ? <p className="errorText"> {userInfoError.jobRole} </p> : null}
                {userInfoError.saveUserData ? <p className="errorText"> {userInfoError.saveUserData} </p> : null}
              </div>
            </div>
          )}
          {/* Next button */}
          <div>
            <button className="nextButton" onClick={!loading ? handleNextButtonChange : null}>
              {" "}
              {loading ? <Loader /> : string.NEXT}
            </button>
          </div>
          {/* Back button */}
          <div>
            <button className="backButton" onClick={handleBackButtonChange}>
              {string.BACK}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PersonalDetails;
